import React, { useEffect, useState } from "react";

export default function Hero({
  title = "Find The Perfect Stay At The Best Prices",
  subtitle = "Discover amazing hotels in Dubai and enjoy unforgettable stays with exclusive deals.",
  backgroundImage,
  backgroundImages = [],
  variant = "inner",
  overlayOpacity = "bg-black/10",
  children,
  slideInterval = 5000,
}) {
  const isHome = variant === "home";

  const [current, setCurrent] = useState(0);

  // =========================================================
  // AUTO SLIDER
  // =========================================================

  useEffect(() => {
    if (!isHome || backgroundImages.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrent((prev) => {
        return (prev + 1) % backgroundImages.length;
      });
    }, slideInterval);

    return () => {
      clearInterval(interval);
    };
  }, [isHome, backgroundImages, slideInterval]);

  // =========================================================
  // ACTIVE BACKGROUND IMAGE
  // =========================================================

  const activeImage =
    backgroundImages.length > 0
      ? backgroundImages[current]
      : backgroundImage;

  return (
    <section
      className={`
        relative
        w-full
        overflow-hidden
        transition-all
        duration-700

        ${isHome
          ? "min-h-[620px] sm:min-h-[650px] md:min-h-[660px] lg:min-h-[640px] xl:min-h-[650px]"
          : "min-h-[240px] md:min-h-[340px]"
        }
      `}
    >
      {/* =====================================================
          BACKGROUND SLIDER
      ===================================================== */}

      {isHome && backgroundImages.length > 0 && (
        <>
          {backgroundImages.map((img, index) => (
            <div
              key={index}
              className="
                absolute
                inset-0
                bg-cover
                bg-center
                bg-no-repeat
                transition-opacity
                duration-1000
                ease-in-out
              "
              style={{
                backgroundImage: `url(${img})`,
                opacity: index === current ? 1 : 0,
              }}
            />
          ))}
        </>
      )}

      {/* =====================================================
          SINGLE BACKGROUND IMAGE
      ===================================================== */}

      {!isHome && activeImage && (
        <div
          className="
            absolute
            inset-0
            bg-cover
            bg-center
            bg-no-repeat
          "
          style={{
            backgroundImage: `url(${activeImage})`,
          }}
        />
      )}

      {/* =====================================================
          HOME FALLBACK BACKGROUND
      ===================================================== */}

      {isHome &&
        backgroundImages.length === 0 &&
        backgroundImage && (
          <div
            className="
              absolute
              inset-0
              bg-cover
              bg-center
              bg-no-repeat
            "
            style={{
              backgroundImage: `url(${backgroundImage})`,
            }}
          />
        )}

      {/* =====================================================
          HERO OVERLAY
          Blue/dark gradient similar to reference design
      ===================================================== */}

      <div
        className={`
          absolute
          inset-0

          ${isHome
            ? "bg-gradient-to-r from-[#031F4F]/75 via-[#073D82]/35 to-transparent"
            : "bg-gradient-to-r from-black/60 via-black/35 to-black/55"
          }
        `}
      />

      {/* =====================================================
          EXTRA TOP BLUE TINT
      ===================================================== */}

      {isHome && (
        <div
          className="
            pointer-events-none
            absolute
            inset-x-0
            top-0
            h-36
            bg-gradient-to-b
            from-[#062D67]/45
            to-transparent
          "
        />
      )}

      {/* =====================================================
          HOME CONTENT
      ===================================================== */}

      {isHome ? (
        <div
          className="
    relative
    z-10
    mx-auto
    flex
    min-h-[620px]
    w-full
    max-w-[1440px]
    flex-col
    justify-end

    px-5
    pt-28
    pb-10

    sm:min-h-[650px]
    sm:px-8
    sm:pt-32
    sm:pb-12

    md:min-h-[660px]
    md:pb-14

    lg:min-h-[640px]
    lg:px-10
    lg:pt-0
    lg:pb-12

    xl:px-12
    xl:pb-14
  "
        >
          {/* =================================================
              HERO TEXT
          ================================================= */}

          <div
            className="
              mb-8
              max-w-[650px]
              text-left

              sm:mb-9

              lg:mb-8
            "
          >
            {/* Badge */}

            <div
              className="
                mb-4
                inline-flex
                items-center
                rounded-md
                bg-[#174EA6]
                px-3
                py-1.5

                text-[10px]
                font-bold
                uppercase
                tracking-wide
                text-white

                shadow-lg
                sm:text-[11px]
              "
            >
              Best Hotel Deals In Dubai
            </div>

            {/* Heading */}

            <h1
              className="
                max-w-[650px]
                text-4xl
                font-extrabold
                leading-[1.08]
                tracking-[-0.02em]
                text-white
                drop-shadow-[0_3px_10px_rgba(0,0,0,0.25)]

                sm:text-5xl

                md:text-5xl

                lg:text-[48px]

                xl:text-[52px]
              "
            >
              {title}
            </h1>

            {/* Subtitle */}

            {subtitle && (
              <p
                className="
                  mt-4
                  max-w-[580px]
                  text-sm
                  font-medium
                  leading-6
                  text-white/90
                  drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]

                  sm:text-base
                  sm:leading-7
                "
              >
                {subtitle}
              </p>
            )}
          </div>

          {/* =================================================
              SEARCH BOX
          ================================================= */}

          {children && (
            <div
              className="
                w-full
                max-w-[1360px]

                rounded-2xl
                bg-white
                p-3

                shadow-[0_15px_45px_rgba(0,0,0,0.20)]

                sm:rounded-[18px]
                sm:p-4

                lg:rounded-[16px]
                lg:p-3
              "
            >
              {children}
            </div>
          )}
        </div>
      ) : (
        /* ===================================================
           INNER PAGE HERO
        =================================================== */

        <div
          className="
            relative
            z-10
            mx-auto
            flex
            min-h-[240px]
            w-full
            max-w-[1440px]
            items-center
            justify-center
            px-5
            py-16
            text-center

            md:min-h-[340px]
            md:px-8
            md:py-20

            lg:px-10
          "
        >
          <div
            className="
              mx-auto
              max-w-4xl
              text-white
            "
          >
            {/* Inner page badge */}

            <div
              className="
                mb-4
                inline-flex
                rounded-md
                bg-[#174EA6]/90
                px-3
                py-1.5
                text-[10px]
                font-bold
                uppercase
                tracking-wide
                text-white
              "
            >
              Dubai Hotel Rates
            </div>

            {/* Title */}

            <h1
              className="
                text-4xl
                font-extrabold
                leading-tight
                tracking-tight
                drop-shadow-lg

                md:text-5xl

                lg:text-6xl
              "
            >
              {title}
            </h1>

            {/* Subtitle */}

            {subtitle && (
              <p
                className="
                  mx-auto
                  mt-4
                  max-w-2xl
                  text-base
                  leading-7
                  text-white/90

                  md:text-lg
                "
              >
                {subtitle}
              </p>
            )}

            {/* Additional content */}

            {children && (
              <div className="mt-7">
                {children}
              </div>
            )}
          </div>
        </div>
      )}

      {/* =====================================================
          SLIDER DOTS
      ===================================================== */}

      {isHome && backgroundImages.length > 1 && (
        <div
          className="
            absolute
            bottom-3
            left-1/2
            z-20
            flex
            -translate-x-1/2
            items-center
            gap-2
          "
        >
          {backgroundImages.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => setCurrent(index)}
              className={`
                h-2
                rounded-full
                transition-all
                duration-300

                ${index === current
                  ? "w-6 bg-white"
                  : "w-2 bg-white/50 hover:bg-white/80"
                }
              `}
            />
          ))}
        </div>
      )}
    </section>
  );
}